/**
 * Shimmer skeleton placeholder for data grids.
 *
 * Renders a header row + N body rows with bar placeholders, matched to the
 * grid's row height (64px) and header height (48px) so the skeleton occupies
 * the same visual space as the eventual grid — no layout jump on data load.
 * Keep these dimensions in sync with `trendzGridTheme.ts`.
 *
 * Pass `columns` shaped like the eventual grid (relative widths) so the
 * placeholder bars align with the real columns underneath.
 */

interface SkeletonColumn {
  /** Tailwind grid track size, e.g. '2fr', '1fr', '120px'. */
  width: string;
  /** Width of the placeholder bar inside the cell, as a Tailwind class. */
  bar?: string;
  /** Skip rendering a bar (for action columns). */
  empty?: boolean;
}

interface Props {
  rows?: number;
  columns: SkeletonColumn[];
}

export function GridSkeleton({ rows = 8, columns }: Props) {
  const template = columns.map((c) => c.width).join(' ');

  return (
    <div className="flex h-full w-full flex-col">
      <div
        className="grid h-[48px] items-center border-b border-border bg-surface-muted/60 px-5"
        style={{ gridTemplateColumns: template }}
      >
        {columns.map((c, i) => (
          <div key={i}>
            {!c.empty && <span className={`skeleton block h-2.5 rounded ${c.bar ?? 'w-20'}`} />}
          </div>
        ))}
      </div>

      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={r}
          className="grid h-[64px] items-center border-b border-border/60 px-5"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((c, i) => (
            <div key={i}>
              {!c.empty && (
                <span className={`skeleton block h-3 rounded ${c.bar ?? 'w-1/2'}`} />
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
